import { useEffect } from "react";
import { LandingNav } from "./LandingNav";
import { LandingFooter } from "./LandingFooter";
import { SparklesIcon } from "../../../ui/icons";
import { APP_RELEASES, APP_VERSION } from "../../../shared/version";
import { usePageMeta } from "../../../hooks/usePageMeta";

interface ReleasesPageProps {
  readonly isDark: boolean;
  readonly onToggleTheme: () => void;
}

function formatDate(date: string): string {
  const [year, month, day] = date.split("-");
  const months = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
  return `${Number(day)} ${months[Number(month) - 1]} ${year}`;
}

export function ReleasesPage({ isDark, onToggleTheme }: ReleasesPageProps) {
  usePageMeta(
    "Nouveautés",
    "Suivez les dernières mises à jour de Palnia : nouvelles fonctionnalités, améliorations et corrections."
  );
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-surface text-ink antialiased overflow-x-hidden">
      <LandingNav isDark={isDark} onToggleTheme={onToggleTheme} />

      <main className="max-w-2xl mx-auto px-6 pt-24 pb-16">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
            <SparklesIcon className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-ink">Nouveautés</h1>
            <p className="text-sm text-ink-muted">Historique des mises à jour de Palnia</p>
          </div>
        </div>

        {APP_RELEASES.length === 0 ? (
          <p className="text-sm text-ink-muted text-center py-16">
            Aucune note de version disponible.
          </p>
        ) : (
          <div className="flex flex-col gap-0">
            {APP_RELEASES.map((release, idx) => {
              const isCurrent = release.version === APP_VERSION;
              const isLatest = idx === 0;

              return (
                <div
                  key={release.version}
                  className={`relative pl-8 pb-8 ${idx < APP_RELEASES.length - 1 ? "border-l-2 border-border/50 ml-3" : "ml-3"}`}
                >
                  {/* Timeline dot */}
                  <div className={`absolute -left-[9px] top-0.5 w-4 h-4 rounded-full border-2 ${
                    isLatest
                      ? "bg-accent border-accent"
                      : "bg-surface border-border"
                  }`} />

                  <div className={`rounded-xl p-5 ${isLatest ? "bg-accent/5 border border-accent/20" : "bg-surface-alt/50 border border-border/40"}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-sm font-bold px-2.5 py-0.5 rounded-lg ${
                        isLatest
                          ? "bg-accent text-white"
                          : "bg-surface-alt text-ink"
                      }`}>
                        v{release.version}
                      </span>
                      {isCurrent && (
                        <span className="text-[10px] font-medium text-accent bg-accent/10 px-2 py-0.5 rounded-md">
                          version actuelle
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-ink-faint mb-3">
                      {formatDate(release.date)}
                    </p>

                    <ul className="flex flex-col gap-2">
                      {release.changes.map((change, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-ink-muted leading-relaxed">
                          <span className={`shrink-0 mt-2 w-1.5 h-1.5 rounded-full ${isLatest ? "bg-accent" : "bg-ink-faint/40"}`} />
                          {change}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <LandingFooter />
    </div>
  );
}
